// MEGABYTE Act evidence, and the FITARA scorecard categories AIEA can speak to.
//
// The unevidenced requirement is shown as prominently as the four that pass.
// Training cannot be inferred from any field this platform holds, and a
// compliance view that quietly rounds an absent record up to a pass is the one
// that costs a customer their grade rather than improving it.
import React, { useEffect, useState } from "react";
import { Scale, AlertTriangle } from "lucide-react";
import * as api from "../../api/tra";

const TONE = {
  evidenced:     { fg: "var(--cares-green)", bg: "color-mix(in srgb, var(--cares-green) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-green) 30%, var(--cares-surface-card))", label: "Evidenced" },
  partial:       { fg: "var(--cares-amber)", bg: "color-mix(in srgb, var(--cares-amber) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-amber) 30%, var(--cares-surface-card))", label: "Partial" },
  not_evidenced: { fg: "var(--cares-orange)", bg: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", bd: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", label: "Not evidenced" },
};

const REL = { direct: { fg: "var(--cares-purple)", bg: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))" }, supporting: { fg: "var(--cares-text-label)", bg: "var(--cares-surface-board)" } };

export default function MegabytePage() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.getMegabyte().then(setD).catch((e) => setErr(e.message || String(e))); }, []);
  if (err) return <div style={{ padding: 20, color: "var(--cares-orange)" }}>{err}</div>;
  if (!d) return <div style={{ padding: 20, color: "var(--cares-text-label)" }}>Loading…</div>;
  const s = d.summary;

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cares-text-1)", margin: "0 0 4px",
                   display: "flex", alignItems: "center", gap: 9 }}>
        <Scale size={20} style={{ color: "var(--cares-purple)" }} /> MEGABYTE &amp; FITARA Evidence
      </h1>
      <p style={{ fontSize: 13, color: "var(--cares-text-label)", margin: "0 0 6px", maxWidth: 800 }}>
        {d.source.statute} &mdash; graded in the software licensing category of the{" "}
        {d.source.scorecard.replace("FITARA Scorecard — ", "FITARA Scorecard, ")}.
      </p>
      <p style={{ fontSize: 11.5, color: "var(--cares-text-note)", margin: "0 0 16px", maxWidth: 800 }}>
        {d.source.note}
      </p>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        {[["Evidenced", s.evidenced, "#047857"], ["Partial", s.partial, "#b45309"],
          ["Not evidenced", s.not_evidenced, s.not_evidenced ? "#b91c1c" : "#047857"]].map(([l, v, c]) => (
          <div key={l} style={{ background: "var(--cares-surface-card)", border: "1px solid var(--cares-line-card)", borderRadius: 8,
                                padding: "10px 15px", minWidth: 130 }}>
            <div style={{ fontSize: 11, color: "var(--cares-text-label)" }}>{l}</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: c }}>{v}</div>
            <div style={{ fontSize: 10.5, color: "var(--cares-text-note)" }}>of {s.total} requirements</div>
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "var(--cares-text-note)", margin: "0 0 16px", maxWidth: 700 }}>{s.note}</div>

      <div style={{ display: "grid", gap: 10, marginBottom: 24 }}>
        {d.requirements.map((r) => {
          const t = TONE[r.status];
          return (
            <div key={r.requirement}
                 style={{ background: "var(--cares-surface-card)", border: "1px solid var(--cares-line-card)", borderRadius: 8,
                          padding: 15, borderLeft: `3px solid ${t.fg}` }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9, flexWrap: "wrap" }}>
                <span style={{ fontFamily: "monospace", fontSize: 11, color: "var(--cares-text-note)" }}>
                  ({r.requirement})
                </span>
                <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--cares-text-1)" }}>{r.name}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                               color: t.fg, background: t.bg, border: `1px solid ${t.bd}` }}>
                  {t.label}
                </span>
              </div>
              <div style={{ fontSize: 12, color: "var(--cares-text-label)", margin: "5px 0 9px" }}>{r.statute}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cares-text-1)", marginBottom: 7 }}>
                {r.metric}
              </div>
              <ul style={{ margin: 0, paddingLeft: 17, fontSize: 12, color: "var(--cares-text-3)" }}>
                {r.evidence.map((e, i) => <li key={i} style={{ marginBottom: 2 }}>{e}</li>)}
              </ul>
              {r.gap && (
                <div style={{ marginTop: 9, padding: "9px 11px", borderRadius: 5, background: t.bg,
                              border: `1px solid ${t.bd}`, fontSize: 12, color: t.fg,
                              display: "flex", gap: 8 }}>
                  <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{r.gap}</span>
                </div>
              )}
              <div style={{ marginTop: 8, fontFamily: "monospace", fontSize: 10.5, color: "var(--cares-text-note)" }}>
                {r.source !== "—" ? `scored from ${r.source}` : "no AIEA source"}
              </div>
            </div>
          );
        })}
      </div>

      <h2 style={{ fontSize: 16, fontWeight: 700, color: "var(--cares-text-1)", margin: "0 0 10px" }}>
        FITARA scorecard categories
      </h2>
      <div style={{ display: "grid", gap: 9 }}>
        {d.fitaraCategories.map((c) => {
          const rel = REL[c.relevance] || REL.supporting;
          return (
            <div key={c.category} style={{ background: "var(--cares-surface-card)", border: "1px solid var(--cares-line-card)",
                                           borderRadius: 8, padding: 13 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 9 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--cares-text-1)" }}>{c.category}</span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "1px 7px", borderRadius: 999,
                               color: rel.fg, background: rel.bg }}>{c.relevance}</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--cares-text-3)", margin: "5px 0 5px" }}>{c.note}</div>
              <div style={{ fontFamily: "monospace", fontSize: 11.5, color: "var(--cares-purple)" }}>
                {c.evidence}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
